(() => {
  const instances = new WeakMap();

  class ProductScrollControls {
    constructor(root) {
      this.root = root;
      this.onStickyBuyClick = this.onStickyBuyClick.bind(this);
      this.onScrollTopClick = this.onScrollTopClick.bind(this);
      this.onVariantLinkClick = this.onVariantLinkClick.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.sectionId = root.dataset.section;
      this.productFormId = root.dataset.productFormId;
      this.variantsAnchorId = root.dataset.variantsAnchor;
      this.stickyMode = root.dataset.stickyMode || 'description';
      this.descriptionInView = false;
      this.buyBoxOutOfView = false;
      this.scrollingDown = true;
      this.currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

      this.panel = root.querySelector('[data-sticky-panel]');
      this.button = root.querySelector('[data-sticky-buy]');
      this.buttonLabel = root.querySelector('[data-sticky-buy-label]');
      this.spinner = root.querySelector('[data-sticky-buy-spinner]');
      this.fab = root.querySelector('[data-scroll-top]');
      this.variantLink = root.querySelector('[data-sticky-variant-link]');
      this.mediaWrap = root.querySelector('[data-sticky-media]');
      this.image = root.querySelector('[data-sticky-image], .sticky-buy-bar__image');
      this.priceTarget = root.querySelector(`#StickyBuyBarPrice-${this.sectionId}`);
      this.mainForm = document.getElementById(this.productFormId);
      this.mainSubmit = this.mainForm?.querySelector('[name="add"]');
      this.variantInput = this.mainForm?.querySelector('input[name="id"]');
      this.mainPrice = document.getElementById(`price-${this.sectionId}`);
      this.description =
        document.getElementById(root.dataset.descriptionId) ||
        document.querySelector(
          `#MainProduct-${this.sectionId} [data-product-description], #MainProduct-${this.sectionId} .product__description-wide`
        );
      this.variantsData = this.parseVariantsData();
      // Permanent mode: observe the ATC form only — ProductInfo includes FAQ/description
      // and would keep intersecting long after the buy box leaves the viewport.
      this.buyBox =
        this.mainForm ||
        document.querySelector(
          `#ProductInfo-${this.sectionId} product-form, #ProductInfo-${this.sectionId} .product-form`
        ) ||
        document.getElementById(`ProductInfo-${this.sectionId}`);

      if (!this.panel || !this.button) return;
      if (this.stickyMode !== 'permanent' && !this.description) return;

      this.button.addEventListener('click', this.onStickyBuyClick);
      this.fab?.addEventListener('click', this.onScrollTopClick);
      this.variantLink?.addEventListener('click', this.onVariantLinkClick);
      this.variantInput?.addEventListener('change', this.onVariantInputChange);
      this.toggleBuyBar(false);
      this.toggleFab(false);

      if (this.stickyMode === 'permanent' && this.buyBox) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.buyBoxOutOfView = !entry.isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(this.buyBox);
      } else if (this.description) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.descriptionInView = entry.isIntersecting;
            this.updateVisibility();
          },
          { threshold: 0, rootMargin: '0px' }
        );
        this.observer.observe(this.description);
        this.syncDescriptionInView();
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.syncDescriptionInView();
      this.updateVisibility();

      if (this.variantInput?.value) {
        this.syncStickyImage(this.variantInput.value);
        this.syncStickyPrice(this.variantInput.value);
      }

      if (this.mainSubmit) {
        this.syncFromMainSubmit();
        this.mainSubmitObserver = new MutationObserver(() => this.syncFromMainSubmit());
        this.mainSubmitObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });
      }

    }

    destroy() {
      this.observer?.disconnect();
      this.mainSubmitObserver?.disconnect();
      this.priceObserver?.disconnect();
      this.button?.removeEventListener('click', this.onStickyBuyClick);
      this.fab?.removeEventListener('click', this.onScrollTopClick);
      this.variantLink?.removeEventListener('click', this.onVariantLinkClick);
      this.variantInput?.removeEventListener('change', this.onVariantInputChange);
      window.removeEventListener('scroll', this.onScroll);
      document.body.classList.remove('sticky-buy-bar-visible');
    }

    parseVariantsData() {
      const script = this.root.querySelector('[data-sticky-variants]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error(error);
        return [];
      }
    }

    syncDescriptionInView() {
      if (!this.description) return;
      const rect = this.description.getBoundingClientRect();
      this.descriptionInView = rect.top < window.innerHeight && rect.bottom > 0;
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > this.currentScrollTop + 2) {
        this.scrollingDown = true;
      } else if (scrollTop < this.currentScrollTop - 2) {
        this.scrollingDown = false;
      }

      this.currentScrollTop = scrollTop;
      this.syncDescriptionInView();
      this.updateVisibility();
    }

    updateVisibility() {
      if (this.stickyMode === 'permanent') {
        this.toggleBuyBar(this.buyBoxOutOfView);
        this.toggleFab(false);
        return;
      }

      const active = this.descriptionInView;
      const showBuyBar = active && this.scrollingDown;
      const showFab = active && !this.scrollingDown;

      this.toggleBuyBar(showBuyBar);
      this.toggleFab(showFab);
    }

    shouldYieldToReleasit() {
      if (window.matchMedia('(max-width: 749px)').matches) return true;
      const rsi = document.querySelector('#rsi_buy_now_button.rsi_floating, .rsi_floating_bottom');
      if (!rsi) return false;
      const cs = getComputedStyle(rsi);
      return cs.position === 'fixed' && cs.display !== 'none' && cs.visibility !== 'hidden';
    }

    toggleBuyBar(show) {
      if (show && this.shouldYieldToReleasit()) show = false;
      if (this.isBuyBarVisible === show) return;
      this.isBuyBarVisible = show;
      this.panel.classList.toggle('is-visible', show);
      this.panel.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.panel.toggleAttribute('inert', !show);
      document.body.classList.toggle('sticky-buy-bar-visible', show);
    }

    toggleFab(show) {
      if (!this.fab || this.isFabVisible === show) return;
      this.isFabVisible = show;
      this.fab.classList.toggle('is-visible', show);
      this.fab.setAttribute('aria-hidden', show ? 'false' : 'true');
      this.fab.toggleAttribute('inert', !show);
    }

    syncStickyPrice(variantId) {
      if (!this.priceTarget || !variantId) return;

      const variant = this.variantsData.find((item) => String(item.id) === String(variantId));
      if (!variant?.price) return;

      this.priceTarget.innerHTML = `<span class="sticky-buy-bar__price-current">${variant.price}</span>`;
    }

    syncFromMainSubmit() {
      if (!this.mainSubmit || !this.button) return;

      const disabled = this.mainSubmit.hasAttribute('disabled');
      this.button.toggleAttribute('disabled', disabled);

      const isLoading = this.mainSubmit.classList.contains('loading');
      this.button.classList.toggle('loading', isLoading);
      this.spinner?.classList.toggle('hidden', !isLoading);
    }

    syncStickyImage(variantId) {
      if (!this.image || !variantId) return;

      const variant = this.variantsData.find((item) => String(item.id) === String(variantId));
      if (!variant?.src) return;

      this.image.src = variant.src;
      if (variant.srcSmall) {
        this.image.srcset = `${variant.srcSmall} 60w, ${variant.src} 120w`;
      } else {
        this.image.removeAttribute('srcset');
      }
      this.image.alt = variant.alt || '';
      this.mediaWrap?.removeAttribute('hidden');
    }

    onVariantInputChange(event) {
      this.syncStickyImage(event.target.value);
      this.syncStickyPrice(event.target.value);
    }

    onVariantLinkClick(event) {
      event.preventDefault();
      const target =
        document.getElementById(this.variantsAnchorId) ||
        document.querySelector(
          `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
        );
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    onStickyBuyClick() {
      // Scroll to payment buttons area instead of adding to cart
      const paymentArea = document.getElementById(`ProductInfo-${this.sectionId}`);
      if (paymentArea) {
        // Try to find the buy buttons within the product info for more precise scroll
        const buyButtons = paymentArea.querySelector('.product-form, [data-shopify-button-container], .buy-buttons');
        (buyButtons || paymentArea).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }

    onScrollTopClick() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-product-scroll-controls]').forEach((el) => {
      instances.get(el)?.destroy();
      instances.set(el, new ProductScrollControls(el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
